/**
 * PDP Add to Cart Button Web Component
 * Handles add to cart functionality with loading states for product forms
 */
export default class PDPAddToCartButton extends HTMLElement {
  constructor() {
    super();
    this.form = null;
    this.submitButton = null;
    this.defaultText = '';
    this.addingText = '';
    this.boundRestoreButtonState = this.restoreButtonState.bind(this);
    this.boundHandleSubmit = this.handleSubmit.bind(this);
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    this.cleanup();
  }

  init() {
    // Find the form - either parent form or form within this element
    this.form = this.closest('form') || this.querySelector('form');
    
    if (!this.form) {
      console.warn('PDPAddToCartButton: No form found');
      return;
    }

    // Find the submit button
    this.submitButton = this.form.querySelector('[type="submit"]');
    
    if (!this.submitButton) {
      console.warn('PDPAddToCartButton: No submit button found in form');
      return;
    }

    // Set up button text configuration
    this.defaultText = this.submitButton.getAttribute('data-add-to-cart-text') || 
                      this.readLabel() || 
                      (window.Theme?.translations?.add_to_cart || 'Add to cart');
    this.addingText = this.submitButton.getAttribute('data-adding-text') || 
                     (window.Theme?.translations?.adding_to_cart || 'Adding...');

    // Add event listeners
    this.form.addEventListener('submit', this.boundHandleSubmit);
    document.addEventListener('cart:updated', this.boundRestoreButtonState, { passive: true });
  }

  cleanup() {
    if (this.form) {
      this.form.removeEventListener('submit', this.boundHandleSubmit);
    }
    document.removeEventListener('cart:updated', this.boundRestoreButtonState);
  }

  // Helpers to read/write visible label for both <button> and <input type="submit">
  readLabel() {
    if (!this.submitButton) return '';

    if (this.submitButton.tagName === 'INPUT') {
      return this.submitButton.value;
    }

    // plp-card span structure — read only the text span, not the price
    const plpTextSpan = this.submitButton.querySelector('.plp-card__button-text');
    if (plpTextSpan) {
      return plpTextSpan.textContent.trim();
    }

    // Generic button with separate price span — exclude price from label
    const priceSpan = this.submitButton.querySelector('.button__price');
    if (priceSpan) {
      const clone = this.submitButton.cloneNode(true);
      clone.querySelector('.button__price')?.remove();
      return clone.textContent.trim();
    }

    return this.submitButton.textContent.trim() || '';
  }

  writeLabel(text) {
    if (!this.submitButton) return;

    if (this.submitButton.tagName === 'INPUT') {
      this.submitButton.value = text;
      return;
    }

    // plp-card span structure — update text span in place, leave price span untouched
    const plpTextSpan = this.submitButton.querySelector('.plp-card__button-text');
    if (plpTextSpan) {
      plpTextSpan.textContent = text;
      return;
    }

    // Generic button with separate price span
    const priceSpan = this.submitButton.querySelector('.button__price');
    if (priceSpan) {
      const priceHTML = priceSpan.outerHTML;
      const textNode = document.createTextNode(text + ' ');
      this.submitButton.innerHTML = '';
      this.submitButton.appendChild(textNode);
      this.submitButton.insertAdjacentHTML('beforeend', priceHTML);
    } else {
      this.submitButton.textContent = text;
    }
  }

  restoreButtonState() {
    if (!this.submitButton) return;
    
    this.writeLabel(this.defaultText);
    this.submitButton.disabled = false;
    this.submitButton.removeAttribute('aria-busy');
  }
  
  getSelectedCompareAtPrice() {
    const selectedRadio = this.form.querySelector('.js-pdp-qty-breaks:checked');
    if (selectedRadio && selectedRadio.dataset.comparePrice) {
      return selectedRadio.dataset.comparePrice;
    }
    return null;
  }

  updateCompareAtPrice() {
    const comparePrice = this.getSelectedCompareAtPrice();
    const comparePriceInput = this.form.querySelector('input[name="properties[_compare_at_price]"]');
    
    if (comparePriceInput && comparePrice !== null) {
      comparePriceInput.value = comparePrice;
    }
  }

  async handleSubmit(e) {
    e.preventDefault();

    if (!this.submitButton || !this.form) return;
    const errorMessage = e.target.querySelector('.product-form-text__error');
    this.updateCompareAtPrice();
    // Set loading state
    this.writeLabel(this.addingText);
    this.submitButton.disabled = true;
    this.submitButton.setAttribute('aria-busy', 'true');

    const formData = new FormData(this.form);

    try {
      const response = await fetch(Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'application/json',
        },
      });

      const data = await response.json();
      if (!response.ok) {
        this.showError(
          data.message,
          errorMessage
        );
        throw new Error(data.message);
      }
      
      // Update cart if global functions exist
      if (typeof window.getCart === 'function') {
        await window.getCart();
      }
      if (typeof window.toggleMiniCart === 'function') {
        window.toggleMiniCart('open');
      }

      // Dispatch cart updated event
      document.dispatchEvent(new CustomEvent('cart:updated'));
      
      // Dispatch custom event from this component
      this.dispatchEvent(new CustomEvent('pdp:added-to-cart', {
        bubbles: true,
        detail: { form: this.form, button: this.submitButton }
      }));

    } catch (err) {
      console.error('Add to cart error:', err);
      
      // Dispatch error event
      this.dispatchEvent(new CustomEvent('pdp:add-to-cart-error', {
        bubbles: true,
        detail: { error: err, form: this.form, button: this.submitButton }
      }));
    } finally {
      this.restoreButtonState();
    }
  }

  showError(message, errorEl) {
    if (!errorEl || !message) return;
    const errormsg = errorEl.querySelector('.error-message');
    errormsg.textContent = message;
    errorEl.classList.remove('hidden');
    errorEl.classList.add('flex');

    clearTimeout(this.errorTimeout);
    this.errorTimeout = setTimeout(() => {
      errormsg.textContent = '';
      errorEl.classList.add('hidden');
      errorEl.classList.remove('flex');
    }, 3000);
  }

}

// Auto-register the component if not already registered
if (!customElements.get('pdp-add-to-cart-button')) {
  customElements.define('pdp-add-to-cart-button', PDPAddToCartButton);
}